import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';
import { ME_QUERY_KEY } from './auth';

export interface Invitation {
  id: number;
  organization_id: number;
  organization_name: string;
  organization_slug: string;
  email: string;
  role: string;
  token: string;
  invited_by_user_id: number | null;
  invited_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  accept_url: string;
}

export interface PublicInvitation {
  organization_name: string;
  organization_slug: string;
  email: string;
  role: string;
  expires_at: string;
  is_expired: boolean;
  is_used: boolean;
  is_revoked: boolean;
}

const invitesKey = (slug: string | undefined) => ['invitations', slug] as const;

export function useInvitations(orgSlug: string | undefined) {
  return useQuery<Invitation[]>({
    queryKey: invitesKey(orgSlug),
    queryFn: () => api.get<Invitation[]>(`/api/orgs/${orgSlug}/invitations`),
    enabled: Boolean(orgSlug),
  });
}

export function useCreateInvitation(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: string }) =>
      api.postJson<Invitation>(`/api/orgs/${orgSlug}/invitations`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesKey(orgSlug) }),
  });
}

export function useRevokeInvitation(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: number) =>
      api.delete<unknown>(`/api/orgs/${orgSlug}/invitations/${invitationId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesKey(orgSlug) }),
  });
}

export function useInvitationLookup(token: string | undefined) {
  return useQuery<PublicInvitation>({
    queryKey: ['invitation', token],
    queryFn: () => api.get<PublicInvitation>(`/api/invitations/${token}`),
    enabled: Boolean(token),
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api.postJson<{ organization_slug: string; organization_name: string }>(
        `/api/invitations/${token}/accept`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}

export function useAcceptInvitationAsNew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      token,
      ...payload
    }: {
      token: string;
      email: string;
      password: string;
      name?: string;
    }) => api.postJson<unknown>(`/api/invitations/${token}/accept-as-new`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });
}
